/**
 * Put-Away Workflow Simulator
 * Simulates the complete put-away workflow using real API calls
 */

import { getSharedRateLimiter } from '../services/RateLimiter';
import { VirtualOperator } from '../services/VirtualOperator';
import { AuthService } from '../../../../../services/authService';
import { SimulatorConfig } from '../types';

// API base URL - use environment variable for production deployments
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Authenticated fetch helper using AuthService
const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  return AuthService.authenticatedFetch(url, options);
};

export interface PutAwayWorkflowResult {
  success: boolean;
  stcOrderNo?: number;
  linesProcessed: number;
  quantityMoved: number;
  error?: string;
  duration: number;
}

export class PutAwayWorkflow {
  private config: SimulatorConfig;
  private operator: VirtualOperator;
  private rateLimiter = getSharedRateLimiter();

  constructor(config: SimulatorConfig, operator: VirtualOperator) {
    this.config = config;
    this.operator = operator;
  }

  /**
   * Execute one complete put-away workflow cycle
   */
  async execute(): Promise<PutAwayWorkflowResult> {
    const startTime = Date.now();
    let stcOrderNo: number | undefined;
    let linesProcessed = 0;
    let quantityMoved = 0;

    try {
      // Step 1: Get available put-away orders
      this.operator.startTask('putaway', 'Henter plasseringsordrer');
      await this.rateLimiter.throttle();

      let availableOrders = await this.getAvailablePutAwayOrders();

      // Step 1b: If no orders exist, try to create from pending relocation records
      if (availableOrders.length === 0) {
        this.operator.startTask('putaway', 'Sjekker grunnlag for utlegg...');
        await this.rateLimiter.throttle();

        const pendingRelocations = await this.getPendingRelocationRecords();

        if (pendingRelocations.length > 0) {
          this.operator.startTask('putaway', `Oppretter utleggsordre fra ${pendingRelocations.length} grunnlag`);
          await this.rateLimiter.throttle();

          // Get RelIDs from pending relocations
          const relIds = pendingRelocations.map((r: any) => r.relID || r.RelID).filter(Boolean);

          if (relIds.length > 0) {
            const createResult = await this.createPutAwayFromRelocation(relIds);
            if (createResult.success && createResult.stcOrderNo) {
              // Wait for order to be available
              await this.delay(500);
              availableOrders = await this.getAvailablePutAwayOrders();
            }
          }
        }
      }

      if (availableOrders.length === 0) {
        return {
          success: true,
          linesProcessed: 0,
          quantityMoved: 0,
          duration: Date.now() - startTime,
          error: 'Ingen plasseringsordrer tilgjengelig'
        };
      }

      // Step 2: Select a random order
      const selectedOrder = availableOrders[Math.floor(Math.random() * availableOrders.length)];
      stcOrderNo = selectedOrder.stcOrderNo ?? selectedOrder.StcOrderNo ?? selectedOrder.stcorderNo;

      if (stcOrderNo === undefined) {
        return {
          success: false,
          linesProcessed: 0,
          quantityMoved: 0,
          duration: Date.now() - startTime,
          error: 'Kunne ikke finne stcOrderNo i valgt ordre'
        };
      }

      // Step 3: Lock the order
      this.operator.startTask('putaway', `Låser ordre ${stcOrderNo}`);
      await this.rateLimiter.throttle();
      await this.lockPutAwayOrder(stcOrderNo);

      try {
        // Step 4: Get order details with lines
        this.operator.startTask('putaway', `Henter detaljer for ${stcOrderNo}`);
        await this.rateLimiter.throttle();
        const orderDetails = await this.getPutAwayOrderDetails(stcOrderNo);
        const lines = orderDetails.lines || [];

        // Step 5: Process each line - complete with bin location to update ItemBins
        for (const line of lines) {
          // Skip already processed lines (LineStatus 60 = Complete)
          const lineStatus = line.lineStatus ?? line.LineStatus ?? 0;
          if (lineStatus >= 60 || (line.quantityMoved && line.quantityMoved >= line.quantity)) {
            continue;
          }

          // Get the target bin from the line (set by backend during order creation)
          let toBinCode = line.toBinCode ?? line.ToBinCode ?? '';
          const warehouseId = line.warehouseID ?? line.WarehouseID ?? this.config.warehouseId;
          const qtyToMove = line.quantity - (line.quantityMoved || 0);

          // If no valid bin assigned (backend uses "[X]" as placeholder), select a random storage bin
          if (!toBinCode || toBinCode === '[X]' || toBinCode === '') {
            const randomBin = await this.selectRandomStorageBin();
            if (!randomBin) {
              console.warn(`[PutAwayWorkflow] Line ${line.stcLineNo}: No bin assigned and no storage bins available`);
              continue;
            }
            toBinCode = randomBin;
            console.log(`[PutAwayWorkflow] Line ${line.stcLineNo}: No bin assigned, selected random bin: ${toBinCode}`);
          }

          this.operator.startTask('putaway', `Plasserer ${line.itemNo} til ${toBinCode}`);

          // Simulate walking to location
          await this.delay(this.config.timing.walkTimeMs);

          // Simulate placing items
          await this.delay(this.config.timing.placeTimeMs);

          // Complete the line with bin - this updates ItemBins via domain events
          await this.rateLimiter.throttle();
          await this.completePutAwayLineWithBin(
            stcOrderNo,
            line.stcLineNo,
            warehouseId,
            toBinCode,
            qtyToMove
          );

          linesProcessed++;
          quantityMoved += qtyToMove;

          console.log(`[PutAwayWorkflow] Completed line ${line.stcLineNo}: ${line.itemNo} x ${qtyToMove} -> ${toBinCode}`);
        }

        // Step 6: Complete the put-away order (status 60)
        this.operator.startTask('putaway', `Fullfører ordre ${stcOrderNo}`);
        await this.rateLimiter.throttle();
        await this.updatePutAwayOrderStatus(stcOrderNo, 60);

        return {
          success: true,
          stcOrderNo,
          linesProcessed,
          quantityMoved,
          duration: Date.now() - startTime
        };

      } finally {
        // Always unlock the order
        await this.unlockPutAwayOrder(stcOrderNo);
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ukjent feil';
      console.error(`[PutAwayWorkflow] Error:`, errorMessage);

      return {
        success: false,
        stcOrderNo,
        linesProcessed,
        quantityMoved,
        duration: Date.now() - startTime,
        error: errorMessage
      };
    }
  }

  /**
   * Get available put-away orders (status 10)
   */
  private async getAvailablePutAwayOrders(): Promise<any[]> {
    const response = await authenticatedFetch(`${API_BASE_URL}/intrelorders/status/10`);

    if (!response.ok) {
      throw new Error(`Failed to get put-away orders: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get pending relocation records (Grunnlag) that can be used to create put-away orders
   */
  private async getPendingRelocationRecords(): Promise<any[]> {
    try {
      // Get relocations with status 20 (pending) and filter by warehouse
      const response = await authenticatedFetch(`${API_BASE_URL}/intrelocations/status/20`);

      if (!response.ok) {
        return [];
      }

      const relocations = await response.json();

      // Filter to this warehouse only
      return relocations.filter((r: any) =>
        (r.warehouseID === this.config.warehouseId || r.WarehouseID === this.config.warehouseId)
      );
    } catch (error) {
      console.warn('[PutAwayWorkflow] Could not get pending relocations:', error);
      return [];
    }
  }

  /**
   * Create put-away order from relocation records
   */
  private async createPutAwayFromRelocation(relIds: string[]): Promise<{ success: boolean; stcOrderNo?: number }> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/putaway/create-from-relocation-batch`,
        {
          method: 'POST',
          body: JSON.stringify({
            RelocationIds: relIds
          })
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ errorMessage: response.statusText }));
        console.warn('[PutAwayWorkflow] Could not create put-away from relocation:', error.errorMessage);
        return { success: false };
      }

      const result = await response.json();
      return {
        success: result.success,
        stcOrderNo: result.stcOrderNo
      };
    } catch (error) {
      console.error('[PutAwayWorkflow] Error creating put-away from relocation:', error);
      return { success: false };
    }
  }

  /**
   * Get put-away order details
   */
  private async getPutAwayOrderDetails(stcOrderNo: number): Promise<any> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/intrelorders/${stcOrderNo}/details`
    );

    if (!response.ok) {
      throw new Error(`Failed to get put-away order details: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Lock put-away order
   */
  private async lockPutAwayOrder(stcOrderNo: number): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/intrelorders/${stcOrderNo}/lock`,
      {
        method: 'POST',
        body: JSON.stringify({ operatorID: this.operator.getId() })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to lock put-away order: ${response.statusText}`);
    }
  }

  /**
   * Unlock put-away order
   */
  private async unlockPutAwayOrder(stcOrderNo: number): Promise<void> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/unlock`,
        {
          method: 'POST',
          body: JSON.stringify({ operatorID: this.operator.getId() })
        }
      );

      if (!response.ok) {
        console.error(`Failed to unlock put-away order ${stcOrderNo}`);
      }
    } catch (error) {
      console.error(`Error unlocking put-away order ${stcOrderNo}:`, error);
    }
  }

  /**
   * Complete a put-away line with bin location - updates ItemBins via domain events
   * This is the correct method that triggers inventory updates
   */
  private async completePutAwayLineWithBin(
    stcOrderNo: number,
    stcLineNo: number,
    warehouseId: string,
    toBinCode: string,
    quantity: number
  ): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/intrelorders/${stcOrderNo}/lines/${stcLineNo}/complete-with-bin`,
      {
        method: 'POST',
        body: JSON.stringify({
          warehouseID: warehouseId,
          toBinCode: toBinCode,
          quantity: quantity
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.errorMessage || `Failed to complete put-away line: ${response.statusText}`);
    }
  }

  /**
   * Update quantity moved for a line (legacy - does NOT update ItemBins)
   * @deprecated Use completePutAwayLineWithBin instead
   */
  private async updateQuantityMoved(
    stcOrderNo: number,
    stcLineNo: number,
    quantityMoved: number
  ): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/intrelorders/${stcOrderNo}/lines/${stcLineNo}/quantity`,
      {
        method: 'PUT',
        body: JSON.stringify({
          quantityMoved,
          movedBy: this.operator.getId()
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to update quantity moved: ${response.statusText}`);
    }
  }

  /**
   * Update put-away order status
   */
  private async updatePutAwayOrderStatus(stcOrderNo: number, status: number): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/intrelorders/${stcOrderNo}/status`,
      {
        method: 'PUT',
        body: JSON.stringify({ status })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to update put-away order status: ${response.statusText}`);
    }
  }

  /**
   * Select a random storage bin for put-away when no bin is assigned
   */
  private async selectRandomStorageBin(): Promise<string | null> {
    try {
      // Get available bins from the warehouse using correct API endpoint
      const response = await authenticatedFetch(
        `${API_BASE_URL}/stc-bins/company/${encodeURIComponent(this.config.companyNo)}/warehouse/${encodeURIComponent(this.config.warehouseId)}`
      );

      if (!response.ok) {
        console.warn('[PutAwayWorkflow] Could not fetch bins for random selection:', response.status, response.statusText);
        return null;
      }

      const bins = await response.json();
      // StcBinController returns array directly
      const binList = Array.isArray(bins) ? bins : [];

      // Filter out receiving bins - we want storage bins
      const storageBins = binList.filter((bin: any) => {
        const binCode = bin.binCode ?? bin.BinCode ?? bin.locationName ?? '';
        const upperBin = binCode.toUpperCase();
        return !upperBin.includes('MOTTAK') &&
               !upperBin.includes('INN') &&
               !upperBin.includes('RECEIVING') &&
               !upperBin.includes('RCV') &&
               !upperBin.includes('PAKK') &&
               !upperBin.includes('PACK');
      });

      if (storageBins.length === 0) {
        console.warn('[PutAwayWorkflow] No storage bins available');
        return null;
      }

      // Select random bin
      const randomIndex = Math.floor(Math.random() * storageBins.length);
      const selectedBin = storageBins[randomIndex];
      return selectedBin.binCode ?? selectedBin.BinCode ?? selectedBin.locationName ?? null;

    } catch (error) {
      console.error('[PutAwayWorkflow] Error selecting random bin:', error);
      return null;
    }
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default PutAwayWorkflow;
